import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Grid,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconArrowsExchange } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const StockTransfers = () => {
    const [appId, setAppId] = useState(null);
    const [status, setStatus] = useState({ type: '', message: '' });

    const [products, setProducts] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [transfers, setTransfers] = useState([]);

    const [selectedProductId, setSelectedProductId] = useState('');
    const [selectedVariacaoId, setSelectedVariacaoId] = useState('');
    const [warehouseOrigem, setWarehouseOrigem] = useState('');
    const [warehouseDestino, setWarehouseDestino] = useState('');
    const [quantidade, setQuantidade] = useState('');
    const [notas, setNotas] = useState('');

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadProducts();
        loadWarehouses();
        loadTransfers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const loadProducts = async () => {
        const res = await api.get(`v1/apps/${appId}/products/`);
        setProducts(res.data || []);
    };

    const loadWarehouses = async () => {
        const res = await api.get(`v1/apps/${appId}/inventory/warehouses/`);
        setWarehouses(res.data || []);
    };

    const loadTransfers = async () => {
        const res = await api.get(`v1/apps/${appId}/inventory/transfers/`);
        setTransfers(res.data.transfers || []);
    };

    const selectedProduct = products.find((p) => String(p.id) === String(selectedProductId));
    const variacoesDoProduto = selectedProduct?.variacoes || [];

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus({ type: '', message: '' });

        if (!selectedVariacaoId) {
            setStatus({ type: 'error', message: 'Selecione um produto e uma variação' });
            return;
        }
        if (!warehouseOrigem || !warehouseDestino) {
            setStatus({ type: 'error', message: 'Selecione os depósitos de origem e destino' });
            return;
        }
        if (warehouseOrigem === warehouseDestino) {
            setStatus({ type: 'error', message: 'Origem e destino devem ser depósitos diferentes' });
            return;
        }
        if (!quantidade || Number(quantidade) <= 0) {
            setStatus({ type: 'error', message: 'Informe uma quantidade maior que zero' });
            return;
        }

        try {
            await api.post(`v1/apps/${appId}/inventory/transfers/create/`, {
                produto_variacao: selectedVariacaoId,
                warehouse_origem: warehouseOrigem,
                warehouse_destino: warehouseDestino,
                quantidade: Number(quantidade),
                notas
            });
            setStatus({ type: 'success', message: 'Transferência registrada com sucesso!' });
            setSelectedProductId('');
            setSelectedVariacaoId('');
            setWarehouseOrigem('');
            setWarehouseDestino('');
            setQuantidade('');
            setNotas('');
            loadTransfers();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao registrar transferência' });
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconArrowsExchange}
                gradient="coral"
                title="Transferências entre Depósitos"
                subtitle="Move estoque de um depósito pra outro, mantendo o histórico"
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <GlassCard sx={{ mb: 3 }}>
                <CardContent>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Produto"
                                    fullWidth
                                    required
                                    value={selectedProductId}
                                    onChange={(e) => {
                                        setSelectedProductId(e.target.value);
                                        setSelectedVariacaoId('');
                                    }}
                                >
                                    {products.map((p) => (
                                        <MenuItem key={p.id} value={p.id}>
                                            {p.nome}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Variação"
                                    fullWidth
                                    required
                                    disabled={!selectedProductId}
                                    value={selectedVariacaoId}
                                    onChange={(e) => setSelectedVariacaoId(e.target.value)}
                                >
                                    {variacoesDoProduto.map((v) => (
                                        <MenuItem key={v.id} value={v.id}>
                                            {v.nome_variacao} (estoque total: {v.estoque})
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Depósito de origem"
                                    fullWidth
                                    required
                                    value={warehouseOrigem}
                                    onChange={(e) => setWarehouseOrigem(e.target.value)}
                                >
                                    {warehouses.map((w) => (
                                        <MenuItem key={w.id} value={w.id}>
                                            {w.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    select
                                    label="Depósito de destino"
                                    fullWidth
                                    required
                                    value={warehouseDestino}
                                    onChange={(e) => setWarehouseDestino(e.target.value)}
                                >
                                    {warehouses.map((w) => (
                                        <MenuItem key={w.id} value={w.id}>
                                            {w.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Quantidade"
                                    type="number"
                                    fullWidth
                                    required
                                    inputProps={{ min: 1 }}
                                    value={quantidade}
                                    onChange={(e) => setQuantidade(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextField
                                    label="Notas"
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={notas}
                                    onChange={(e) => setNotas(e.target.value)}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Box display="flex" justifyContent="flex-end">
                                    <Button type="submit" variant="contained">
                                        Registrar Transferência
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </CardContent>
            </GlassCard>

            <GlassCard>
                <CardContent>
                    <Stack spacing={1} mb={2}>
                        <strong>Histórico de transferências</strong>
                    </Stack>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Produto</TableCell>
                                <TableCell>Origem</TableCell>
                                <TableCell>Destino</TableCell>
                                <TableCell align="right">Quantidade</TableCell>
                                <TableCell>Data</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {transfers.map((t) => (
                                <TableRow key={t.id}>
                                    <TableCell>{t.produto_variacao_nome}</TableCell>
                                    <TableCell>{t.warehouse_origem_nome}</TableCell>
                                    <TableCell>{t.warehouse_destino_nome}</TableCell>
                                    <TableCell align="right">{t.quantidade}</TableCell>
                                    <TableCell>{new Date(t.created_at).toLocaleString('pt-BR')}</TableCell>
                                </TableRow>
                            ))}
                            {transfers.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        Nenhuma transferência registrada ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>
        </Box>
    );
};

export default StockTransfers;
